import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { coursesService, sessionsService, sessionRegistrationsService } from '../services/firestoreService';
import { analyticsService } from '../services/analyticsService';
import { activityLogService } from '../services/activityLogService';
import SessionCard from '../components/shared/SessionCard';
import './PublicCourseDetail.css';

const PublicCourseDetail = () => {
  const { courseId } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [registrationsMap, setRegistrationsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState({});
  const [shareMessage, setShareMessage] = useState('');

  const normalizeDateValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (value instanceof Date) return value;
    if (value.toDate) return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    return null;
  };

  const formatDateDisplay = (value) => {
    const date = normalizeDateValue(value);
    return date ? date.toLocaleDateString() : 'TBD';
  };

  const buildSessionMeta = (session) => {
    const meta = [];
    if (session.startDate) {
      meta.push({ icon: '📅', label: 'Start', value: formatDateDisplay(session.startDate) });
    }
    if (session.endDate) {
      meta.push({ icon: '🏁', label: 'End', value: formatDateDisplay(session.endDate) });
    }
    if (session.totalDuration) {
      meta.push({ icon: '⏱️', label: 'Duration', value: `${session.totalDuration} hrs` });
    }
    if (session.sessionCost != null && session.sessionCost !== '') {
      const costVal = typeof session.sessionCost === 'number' ? `$${session.sessionCost}` : session.sessionCost;
      meta.push({ icon: '💰', label: 'Cost', value: costVal });
    }
    return meta;
  };

  const renderSessionDetails = (session) => (
    <div className="session-details">
      <div className="detail-row">
        <span className="label">📅 Start Date:</span>
        <span>{formatDateDisplay(session.startDate)}</span>
      </div>
      <div className="detail-row">
        <span className="label">📅 End Date:</span>
        <span>{formatDateDisplay(session.endDate)}</span>
      </div>
      <div className="detail-row">
        <span className="label">⏱️ Total Duration:</span>
        <span>{session.totalDuration} hours</span>
      </div>
    </div>
  );

  const renderSessionActions = (session) => {
    const isRegistered = !!registrationsMap[session.id];
    const registration = registrationsMap[session.id];
    const isPending = registration?.status === 'pending';
    const isActive = registration?.status === 'active';

    // Check if session is archived (past end date)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = session.endDate ? (session.endDate.toDate ? session.endDate.toDate() : new Date(session.endDate)) : null;
    const isArchived = endDate && (endDate.setHours(0, 0, 0, 0), endDate < today);

    if (isArchived) {
      return (
        <div className="session-actions">
          <div className="info-message archived-message">
            ⚠️ Registration is closed for this session as it has ended.
          </div>
        </div>
      );
    }

    if (!user) {
      return (
        <div className="session-actions">
          <button
            onClick={() => navigate('/', { state: { showSignUp: true } })}
            className="register-btn"
          >
            📝 Sign In to Register
          </button>
        </div>
      );
    }

    if (user.type !== 'student') {
      return <div className="session-actions"><div className="info-message">Only students can register for sessions.</div></div>;
    }

    if (!isRegistered) {
      return (
        <div className="session-actions">
          <button
            onClick={() => handleRegister(session.id)}
            disabled={registering[session.id]}
            className="register-btn"
          >
            {registering[session.id] ? 'Registering...' : '📝 Register for Session'}
          </button>
        </div>
      );
    }

    if (isPending) {
      return <div className="session-actions"><div className="reg-message pending">⏳ Your registration is pending approval.</div></div>;
    }

    if (isActive) {
      return <div className="session-actions"><div className="reg-message active">✅ You are registered for this session.</div></div>;
    }

    return null;
  };

  useEffect(() => {
    const loadCourseData = async () => {
      try {
        // Track page view
        analyticsService.trackPageView(`course-${courseId}`);

        // Load course
        const courseData = await coursesService.getCourse(courseId);
        if (!courseData) {
          setLoading(false);
          return;
        }
        setCourse(courseData);

        // Load sessions for this course
        const allSessions = await sessionsService.getAllSessions();
        const courseSessions = allSessions.filter(s => s.courseId === courseId);
        
        // Filter active and upcoming sessions only
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const activeSessions = courseSessions.filter(session => {
          const endDate = new Date(session.endDate);
          endDate.setHours(0, 0, 0, 0);
          return endDate >= today;
        });
        
        setSessions(activeSessions);

        // Load user registrations if logged in
        if (user && user.type === 'student') {
          const registrations = await sessionRegistrationsService.getStudentRegistrations(user.id);
          const regMap = {};
          registrations.forEach(reg => {
            regMap[reg.sessionId] = reg;
          });
          setRegistrationsMap(regMap);
        }
      } catch (error) {
        console.error('Error loading course:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCourseData();
  }, [courseId, user]);

  const handleRegister = async (sessionId) => {
    if (!user) {
      navigate('/', { state: { showSignUp: true } });
      return;
    }

    if (user.type !== 'student') {
      alert('Only students can register for sessions.');
      return;
    }

    setRegistering({ ...registering, [sessionId]: true });
    try {
      await sessionRegistrationsService.registerForSession(sessionId, user.id);
      await activityLogService.logActivity(user.id, activityLogService.ACTIVITY_TYPES.REGISTER_SESSION, {
        sessionId,
        status: 'pending'
      });
      
      setRegistrationsMap({
        ...registrationsMap,
        [sessionId]: {
          sessionId,
          studentId: user.id,
          status: 'pending',
          registeredAt: new Date()
        }
      });
      alert('Successfully registered! Your registration is pending approval.');
    } catch (error) {
      console.error('Error registering for session:', error);
      if (error.message.includes('Already registered')) {
        alert('You are already registered for this session.');
      } else {
        alert(`Error: ${error.message}`);
      }
    } finally {
      setRegistering({ ...registering, [sessionId]: false });
    }
  };

  const handleShare = () => {
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl);
    setShareMessage('✅ Link copied to clipboard!');
    setTimeout(() => setShareMessage(''), 3000);
  };

  const getSessionStatus = (session) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = normalizeDateValue(session.startDate);
    const endDate = normalizeDateValue(session.endDate);

    if (!startDate || !endDate) {
      return { label: '🔵 Upcoming', type: 'upcoming' };
    }

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (startDate <= today && endDate >= today) {
      return { label: '🟢 Active', type: 'active' };
    } else if (startDate > today) {
      return { label: '🔵 Upcoming', type: 'upcoming' };
    } else {
      return { label: '⚫ Past', type: 'past' };
    }
  };

  const getRegistrationStatus = (sessionId) => {
    const registration = registrationsMap[sessionId];
    if (!registration) return null;
    
    switch (registration.status) {
      case 'pending':
        return { label: '⏳ Registration Pending', type: 'pending' };
      case 'active':
        return { label: '✅ Registered', type: 'active' };
      case 'cancelled':
        return { label: '❌ Cancelled', type: 'past' };
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="public-course-detail">
        <div className="public-header public-course-detail-header">
          <div className="header-content">
            <h1>🎒 Learn for Fun</h1>
            <Link to="/" className="home-link">← Back to Home</Link>
          </div>
        </div>
        <div className="course-header">
          <Link to="/sessions" className="back-link">← Back to Sessions</Link>
        </div>
        <div className="loading-state">
          <p>Loading course details...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="public-course-detail">
        <div className="public-header public-course-detail-header">
          <div className="header-content">
            <h1>🎒 Learn for Fun</h1>
            <Link to="/" className="home-link">← Back to Home</Link>
          </div>
        </div>
        <div className="course-header">
          <Link to="/sessions" className="back-link">← Back to Sessions</Link>
        </div>
        <div className="empty-state">
          <h2>Course Not Found</h2>
          <p>The course you're looking for doesn't exist or has been removed.</p>
          <Link to="/sessions" className="cta-button">View All Sessions</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="public-course-detail">
      <div className="public-header public-course-detail-header">
        <div className="header-content">
          <h1>🎒 Learn for Fun</h1>
          <Link to="/" className="home-link">← Back to Home</Link>
        </div>
      </div>
      <div className="course-header">
        <div className="header-nav">
          <Link to="/sessions" className="back-link">← Back to Sessions</Link>
          <Link to="/" className="home-link">Home</Link>
        </div>
        <div className="share-section">
          <button onClick={handleShare} className="share-button">
            📤 Share This Course
          </button>
          {shareMessage && <span className="share-message">{shareMessage}</span>}
        </div>
      </div>

      <div className="course-content">
        <div className="course-hero">
          <h1>{course.title}</h1>
          {course.description && (
            <p className="course-description">{course.description}</p>
          )}
        </div>

        {course.topics && course.topics.length > 0 && (
          <div className="topics-section">
            <h2>📚 Course Topics</h2>
            <div className="topics-grid">
              {course.topics.map((topic, idx) => (
                <div key={idx} className="topic-card">
                  <div className="topic-header">
                    <h3>{topic.name}</h3>
                    {topic.hours && <span className="topic-hours">{topic.hours}h</span>}
                  </div>
                  {topic.description && (
                    <p className="topic-description">{topic.description}</p>
                  )}
                  {topic.sampleCode && (
                    <div className="sample-code">
                      <strong>Example:</strong>
                      <pre><code>{topic.sampleCode}</code></pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="total-duration">
              <strong>Total Course Duration:</strong> {course.topics.reduce((sum, t) => sum + (t.hours || 0), 0)} hours
            </div>
          </div>
        )}

        <div className="sessions-section">
          <h2>🗓️ Available Sessions</h2>
          {sessions.length === 0 ? (
            <div className="no-sessions">
              <p>No active or upcoming sessions for this course at the moment.</p>
              <p>Check back later or <Link to="/sessions">view other available sessions</Link>.</p>
            </div>
          ) : (
            <div className="sessions-list">
              {sessions.map((session) => (
                <SessionCard
                  key={session.id}
                  title={session.title}
                  status={getSessionStatus(session)}
                  secondaryStatus={getRegistrationStatus(session.id)}
                  meta={buildSessionMeta(session)}
                  actions={renderSessionActions(session)}
                >
                  {renderSessionDetails(session)}
                  {session.courseObjects && (
                    <div className="session-info">
                      <strong>🎯 Objectives:</strong>
                      <p>{session.courseObjects}</p>
                    </div>
                  )}
                  {session.expectations && (
                    <div className="session-info">
                      <strong>✨ Expectations:</strong>
                      <p>{session.expectations}</p>
                    </div>
                  )}
                  {session.feesTerms && (
                    <div className="session-info">
                      <strong>💰 Fees & Terms:</strong>
                      <p>{session.feesTerms}</p>
                    </div>
                  )}
                </SessionCard>
              ))}
            </div>
          )}
        </div>

        {!user && (
          <div className="cta-section">
            <h2>Ready to Start Learning?</h2>
            <p>Create an account to register for sessions and track your progress.</p>
            <button 
              onClick={() => navigate('/', { state: { showSignUp: true } })}
              className="cta-button primary"
            >
              ✨ Create Free Account
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicCourseDetail;
